'use client';

/**
 * PurchaseButtonWrapper
 *
 * A wrapper that switches between new PurchaseFlow-based payment processing
 * and legacy PurchaseButton based on the `user.wallet26` flag from Redux.
 *
 * Used for: Advisor Bonus, Quick Topup Banner
 *
 * Key behaviors:
 * - If user has enough credit → call onComplete immediately (no payment needed)
 * - If payment needed → PurchaseFlow in 'button' mode handles everything
 * - Errors/success passed via callbacks to parent
 */

import React, { useCallback, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { getFollowupParam } from '@app/components/purchases/purchases_utils';
import type {
  SupportedCurrency,
  PurchaseButtonWrapperProps,
  PurchaseButtonWrapperReduxState
} from '../types';
import PurchaseButton from '../../advisor_bonus/purchase_button';
import PurchaseFlow from '../PurchaseFlow';
import { isWebProcessedPayment } from '../gateways/componentSelector';
import { withCaptcha, getAppTheme } from '../../../../common/config/utils';
import { purchaseFlowOnLog } from '../../../../common/config/purchaseFlowRenderHelpers';
import { getCurrentLocalization } from '../../../config/i18n';
import { appRoutes } from '../../../config/app_routes';
import { getUser } from '../../../actions/user';
import { setMessageToAppData } from '../../../actions/message_to_app';
import { messageToAppKeys } from '../../../../common/message_to_app_handler';
import classes from './classes.module.scss';
import { toPurchaseFlowTheme } from '../types';

// Re-export props type for external use
export type { PurchaseButtonWrapperProps } from '../types';

// ============================================================================
// Component
// ============================================================================

const PurchaseButtonWrapperComponent: React.FC<PurchaseButtonWrapperProps> = ({
  purchaseAmount,
  onComplete,
  onError,
  disabled = false,
  children,
  className,
  renderStatusPopup = true,
  showSuccessScreen = false,
  showErrorScreen,
  purchaseType = 'advisor_bonus',
  clickSource,
  topupFlow,
  isContinue,
  advisorId,
  onAnalyticsEvent,
  renderIcon,
  renderText,
  forMobileBuyBtnOnClick,
  forMobileAppClickSource,
  onClickHandler,
  clientAvailableCredit = '0',
  wallet26,
  purchasesCount,
  currentPaymentSource,
  purchaseDetails,
  dynamicConfig,
  userCurrencyKey
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const price = purchaseAmount ?? purchaseDetails?.price;
  const requiredCredit = purchaseAmount
    ? Math.max(0, purchaseAmount - parseFloat(clientAvailableCredit))
    : (purchaseDetails?.requiredCredit || 0);

  const fxRate: number = dynamicConfig?.fxRate ?? 1;
  const supportedCurrencies: SupportedCurrency[] = dynamicConfig?.supportedCurrencies || [];
  const currencyKey: string = userCurrencyKey ?? 'USD';

  const hasEnoughCredit = useMemo(() => {
    if (!price) return false;
    return parseFloat(clientAvailableCredit) >= price;
  }, [clientAvailableCredit, price]);

  const metadata = useMemo(() => ({
    advisorId,
    clickSource: clickSource || forMobileAppClickSource,
    topupFlow,
    context: getFollowupParam({ type: purchaseDetails?.purchaseType, topupFlow: purchaseDetails?.topupFlow, isContinue }).context
  }), [advisorId, clickSource, forMobileAppClickSource, topupFlow]);

  const analyticsConfig = useMemo(() => ({
    ...(advisorId ? { advisorId } : {}),
    ...(topupFlow ? { topupFlow } : {}),
    purchasesCount
  }), [advisorId, topupFlow, purchasesCount]);

  const refreshUser = useCallback(() => {
    (dispatch as any)(getUser());
  }, [dispatch]);

  const handleSpendingLimitAction = useCallback(() => {
    if (forMobileBuyBtnOnClick) {
      (dispatch as any)(setMessageToAppData({
        sentToAppMessage: {
          [messageToAppKeys.action]: messageToAppKeys.navigationUpdate,
          [messageToAppKeys.params]: {
            [messageToAppKeys.route]: 'spending_limit'
          }
        }
      }));
      (dispatch as any)(setMessageToAppData({
        sentToAppMessage: {
          [messageToAppKeys.action]: messageToAppKeys.dismiss
        }
      }));
      return;
    }
    window.onbeforeunload = null;
    navigate(t(appRoutes.spendLimits), { state: { clickSource: 'popup' } });
    window.location.reload();
  }, [forMobileBuyBtnOnClick, dispatch, navigate, t]);

  const purchaseFlowConfig = useMemo(() => ({
    renderIcon,
    renderText,
    locale: getCurrentLocalization(),
    theme: toPurchaseFlowTheme(getAppTheme()),
    onLog: purchaseFlowOnLog,
    analytics: analyticsConfig,
    onAnalyticsEvent,
    withCaptcha
  }), [renderIcon, renderText, analyticsConfig, onAnalyticsEvent]);

  const buttonClass = cn(className, classes.button);

  // ==========================================================================
  // Render: Legacy PurchaseButton
  // ==========================================================================

  if (!wallet26) {
    return (
      <PurchaseButton
        purchaseAmount={ purchaseAmount }
        onPurchaseComplete={ onComplete }
        onPurchaseFailed={ () => onError?.('Payment failed') }
        forMobileBuyBtnOnClick={ forMobileBuyBtnOnClick }
        forMobileAppClickSource={ forMobileAppClickSource }
        renderStatusPopup={ renderStatusPopup }
        onClickHandler={ onClickHandler }
        isContinue={ isContinue }
        disabled={ disabled }
        purchaseButtonTitle={ children }
        purchaseButtonTitleClassName={ className }
      />
    );
  }

  // ==========================================================================
  // Render: New Flow — simple button (no payment needed, or mobile CC/PP delegation)
  // ==========================================================================

  const shouldDelegateToMobile = forMobileBuyBtnOnClick
    && requiredCredit > 0
    && !isWebProcessedPayment(currentPaymentSource?.kind);

  const handleSimpleClick = () => {
    if (disabled) return;
    onClickHandler?.();
    if (forMobileBuyBtnOnClick) {
      forMobileBuyBtnOnClick(requiredCredit);
    } else {
      onComplete();
    }
  };

  if (hasEnoughCredit || requiredCredit <= 0 || shouldDelegateToMobile) {
    return (
      <div className={ classes.buttonContainer } data-testid="purchase-button-container">
        <div
          className={ buttonClass }
          onClick={ handleSimpleClick }
          onKeyDown={ (e) => e.key === 'Enter' && handleSimpleClick() }
          role="button"
          tabIndex={ disabled ? -1 : 0 }
          data-testid="purchase-button-wrapper"
        >
          {children}
        </div>
      </div>
    );
  }

  // ==========================================================================
  // Render: New Flow — PurchaseFlow button mode
  // ==========================================================================

  return (
    <div className={ classes.buttonContainer } data-testid="purchase-button-container">
      <PurchaseFlow
        displayMode="button"
        price={ requiredCredit }
        fxRate={ fxRate }
        currencyKey={ currencyKey }
        supportedCurrencies={ supportedCurrencies }
        purchaseType={ purchaseType }
        topupFlow={ topupFlow }
        showStatusPopup={ renderStatusPopup }
        showSuccessScreen={ showSuccessScreen }
        showErrorScreen={ showErrorScreen }
        metadata={ metadata }
        config={ purchaseFlowConfig }
        disabled={ disabled }
        buttonClassName={ buttonClass }
        onComplete={ onComplete }
        onFailed={ (error) => onError?.(error) }
        onCancel={ () => { } }
        onPurchaseSuccess={ refreshUser }
        onSpendingLimitAction={ handleSpendingLimitAction }
        onFlowStarted={ onClickHandler }
      >
        {children}
      </PurchaseFlow>
    </div>
  );
};

// ============================================================================
// Redux Connection
// ============================================================================

const mapStateToProps = (state: PurchaseButtonWrapperReduxState) => ({
  clientAvailableCredit: state.user?.user?.clientAvailableCredit,
  wallet26: state.user?.user?.wallet26,
  purchasesCount: state.user?.user?.purchasesCount,
  currentPaymentSource: state.paymentSources?.current,
  purchaseDetails: state.purchaseDetails,
  dynamicConfig: state.application?.dynamicConfig,
  userCurrencyKey: state.user?.user?.currency
});

export const PurchaseButtonWrapper = connect(mapStateToProps)(PurchaseButtonWrapperComponent);

export default PurchaseButtonWrapper;
